// src/encounter/lib/required-items.ts
// C10.2 — the third sign gate: the REQUIRED items the signer can see that are still unanswered
// (WORKING-AGREEMENT C10.2, rule 8 of the encounter authority: "has unresolved required blanks").
//
// VISIBLE means drawn for THIS signer, by the same chain the step shell renders:
//  1. only the questionnaire steps in the signer's nav — role (HPI/ROS/PE are medical-team only)
//     and the template's `hiddenSteps`, i.e. the page's `resolveSteps` result;
//  2. each step's section is `questionnaireForStep` of the gender-scoped questionnaire the view
//     renders (an unsectioned template: HPI owns everything, other steps draw nothing);
//  3. enableWhen, evaluated by the DS's own `qrItemEnabled` against the whole response; a disabled
//     group hides its subtree (QuestionnaireRenderer `renderItem`);
//  4. a group carrying a group-level itemControl is drawn as a DS BLOCK (chief-complaint, fall,
//     pain, chip panels, vitals…), not as questions, so nothing under it is counted;
//  5. the section's ADDED NOTES narrative is stripped from the renderer and shown only when the
//     notes toggle is opened, so it is not counted;
//  6. only leaf types the renderer draws as an answerable field; `display` answers nothing, and a
//     read-only or calculated item cannot be answered by the signer. A `switch` has no visible
//     unanswered state ("unset draws off"), a `photo` attachment writes nothing without a host
//     `onAttachmentAdd` (the view wires none), and a `choice` with no options draws no control, so
//     none of those is counted either.
// Every exclusion fails OPEN: a gate that blocks a valid sign is worse than no gate.
//
// ANSWERED means a value is present (a whitespace-only string is not) and is not an unaccepted AI
// draft (rule 8: an answer still marked `aiAuthored` names the item, "(not yet accepted)"). "Not applicable",
// "Declined" and "Unable to assess" are authored answer options, so choosing one is a value like
// any other and counts as answered (P13). The QuestionnaireResponse carries no separate
// absent-reason. A symptom chip left "not assessed" has NO value and is unanswered.
import {
  isItemControlRole,
  isRoleValidOn,
  qrItemEnabled,
  type Questionnaire,
  type QItem,
  type QuestionnaireResponse,
  type QRAnswerItem,
} from '@scripsteam/scrips-signal-ds';
import { stepLabel, type PaStepKey } from './encounter-steps';
import type { StepTitleOverrides } from './cps-layout';
import { findNarrativeItem, isQuestionnaireStep, questionnaireForStep } from './questionnaire-groups';

type QAnswerValue = QRAnswerItem['value'];

/** Leaf types QuestionnaireRenderer draws as an answerable field (`renderLeaf`). */
const ANSWERABLE_TYPES: ReadonlySet<QItem['type']> = new Set<QItem['type']>([
  'boolean', 'string', 'text', 'decimal', 'integer', 'quantity', 'date', 'dateTime', 'attachment',
  'choice', 'open-choice',
]);

/** At most this many items are named in the sign toast; the rest are counted. */
export const MAX_NAMED_REQUIRED_ITEMS = 5;

/**
 * How long the sign toast stays up (desk advisory on #622): it names up to five items with their
 * sections, which takes longer to read than the default toast allows.
 */
export const REQUIRED_ITEMS_TOAST_MS = 10_000;

/** One unanswered required item and the step (section) it is drawn in. */
export interface RequiredItemGap {
  item: QItem;
  step: PaStepKey;
  /** The only value is an AI draft the clinician has not accepted (rule 8). */
  aiDraft?: boolean;
}

function isAnswered(value: QAnswerValue): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  // A list is answered only if one element is: `['']` or `['   ']` is as blank as `''`.
  if (Array.isArray(value)) return value.some((v) => isAnswered(v));
  return true;
}

function isBlockGroup(item: QItem): boolean {
  return (
    item.type === 'group' && isItemControlRole(item.itemControl) && isRoleValidOn(item.itemControl, 'group')
  );
}

function isCountable(item: QItem): boolean {
  if (item.required !== true || !ANSWERABLE_TYPES.has(item.type)) return false;
  if (item.readOnly || item.calculatedExpression) return false;
  if (item.type === 'boolean' && item.itemControl === 'switch') return false;
  if (item.type === 'attachment' && item.itemControl === 'photo') return false;
  if (item.type === 'choice' && (item.answerOption?.length ?? 0) === 0) return false;
  return true;
}

/**
 * The required items the signer can see and has not answered, in nav then document order.
 * `questionnaire` is the gender-scoped questionnaire the view renders; `steps` is the signer's
 * resolved nav. No questionnaire → nothing is visible → nothing blocks. No response (or no
 * answer list) → every visible required item is named.
 */
export function unansweredRequiredItems(
  questionnaire: Questionnaire | undefined,
  response: QuestionnaireResponse | undefined,
  steps: readonly PaStepKey[],
): QItem[] {
  return unansweredRequiredItemGaps(questionnaire, response, steps).map((gap) => gap.item);
}

/** As {@link unansweredRequiredItems}, with the step each item is drawn in (for the toast's section). */
export function unansweredRequiredItemGaps(
  questionnaire: Questionnaire | undefined,
  response: QuestionnaireResponse | undefined,
  steps: readonly PaStepKey[],
): RequiredItemGap[] {
  if (!questionnaire) return [];
  // Null prototype: a linkId such as `constructor` or `toString` must not read an inherited value.
  const values: Record<string, QAnswerValue> = Object.create(null);
  // Rule 8: an AI proposal is not documentation until the clinician accepts it. An answer still
  // marked `aiAuthored` is drawn (and drives enableWhen, as on screen) but does not count as answered.
  const aiDrafted = new Set<string>();
  // A missing response or answer list means nothing is answered; it must never throw in doSign.
  for (const a of response?.answers ?? []) {
    if (typeof a?.linkId !== 'string') continue;
    values[a.linkId] = a.value;
    if (a.aiAuthored === true) aiDrafted.add(a.linkId);
    else aiDrafted.delete(a.linkId);
  }
  const missing: RequiredItemGap[] = [];
  const seen = new Set<string>();
  for (const step of steps) {
    if (!isQuestionnaireStep(step)) continue;
    const section = questionnaireForStep(questionnaire, step);
    const narrativeLinkId = findNarrativeItem(section.item)?.linkId;
    const walk = (item: QItem) => {
      if (!qrItemEnabled(item, values)) return;
      if (item.type === 'group') {
        if (!isBlockGroup(item)) (item.item ?? []).forEach(walk);
        return;
      }
      if (item.linkId === narrativeLinkId || seen.has(item.linkId) || !isCountable(item)) return;
      seen.add(item.linkId);
      if (!isAnswered(values[item.linkId])) missing.push({ item, step });
      else if (aiDrafted.has(item.linkId)) missing.push({ item, step, aiDraft: true });
    };
    section.item.forEach(walk);
  }
  return missing;
}

/**
 * The sign toast: the first five items, each prefixed with its section ("HPI: Onset"), then
 * "and N more". The section is the step's nav label (the template's authored title when it has one).
 * An item with no text is "an unlabelled item in HPI" — a linkId is never shown (desk advisory, #622).
 */
export function requiredItemsWarning(gaps: readonly RequiredItemGap[], stepTitles?: StepTitleOverrides): string {
  const labels = gaps.map(({ item, step, aiDraft }) => {
    const section = stepLabel(step, stepTitles);
    const text = item.text?.trim();
    const label = text ? `${section}: ${text}` : `an unlabelled item in ${section}`;
    // User-facing copy never says "AI" (naming lock: Scrips Intelligence). "Accepted" matches the
    // ambient panel's "Accepted · written to chart" (design desk, #622).
    return aiDraft ? `${label} (not yet accepted)` : label;
  });
  const named = labels.slice(0, MAX_NAMED_REQUIRED_ITEMS).join(', ');
  const rest = labels.length - MAX_NAMED_REQUIRED_ITEMS;
  return `Complete the required items before signing: ${named}${rest > 0 ? ` and ${rest} more` : ''}`;
}
